import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ICON_SIZE,
  LabelWithIcon,
  MenuItem,
  SelectWithIcon,
  Stack,
  Switch,
  Typography,
} from '@pulse/ui';
import { useState } from 'react';

import { useBusinessUnitLookup } from '../../organization/hooks/useBusinessUnits';
import { useUpdateMyOrganization } from '../hooks/useUpdateMyOrganization';

// Mandatory BU/Management login gate — a user with neither set is blocked from the rest of the
// app until they set one (see ProtectedRoute). Deliberately the first disableEscapeKeyDown
// dialog in this codebase: no Cancel/close button, Escape and a backdrop click are both
// ignored. Don't "simplify" this back to a normal dismissible dialog — that would defeat the
// story's requirement that a user cannot proceed without completing this.
export function OrganizationGateDialog() {
  const { data: businessUnits = [] } = useBusinessUnitLookup();
  const updateMyOrganization = useUpdateMyOrganization();

  const [isManagement, setIsManagement] = useState(false);
  const [businessUnitId, setBusinessUnitId] = useState('');

  const canSave = isManagement || businessUnitId !== '';

  function handleManagementToggle(next: boolean) {
    setIsManagement(next);
    if (next) setBusinessUnitId('');
  }

  function handleSave() {
    if (!canSave) return;
    updateMyOrganization.mutate({ isManagement, businessUnitId: isManagement ? null : businessUnitId });
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Complete your profile</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Select the Business Unit you belong to. If your role is across multiple Business
            Units, select Management.
          </Typography>

          {updateMyOrganization.isError && (
            <Alert severity="error">{updateMyOrganization.error.message}</Alert>
          )}

          <SelectWithIcon
            icon={<AccountTreeIcon fontSize={ICON_SIZE.sm} />}
            label="Business Unit"
            required={!isManagement}
            disabled={isManagement}
            value={businessUnitId}
            onChange={setBusinessUnitId}
          >
            {businessUnits.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </SelectWithIcon>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box component="span">
              <LabelWithIcon icon={<SupervisorAccountIcon fontSize={ICON_SIZE.sm} />} label="Management" inline />
            </Box>
            <Switch checked={isManagement} onChange={(e) => handleManagementToggle(e.target.checked)} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={!canSave || updateMyOrganization.isPending} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
